import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Modal,
  Chip,
  IconButton,
  Grid,
  Divider,
  Avatar,
  LinearProgress,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  InputAdornment,
  MenuItem,
  CircularProgress,
  ListItemAvatar,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  FormatListBulleted as ListIcon,
  Business as BusinessIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  Edit as EditIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { getToken, base_url } from "../../../config/config";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
/******************************************************
 * THEME COLORS
 ******************************************************/
const lightThemeColors = {
  primary: "#4361ee",
  secondary: "#3a0ca3",
  success: "#2ecc71",
  warning: "#f39c12",
  error: "#e74c3c",
  info: "#3498db",
  background: "#f8f9fd",
  card: "#ffffff",
  text: "#2d3748",
  textSecondary: "#718096",
  columnHeader: "#ebf3ff",
  columnBackground: "#ffffff",
  columnBackgroundHover: "#f7faff",
  taskBorder: "#e2e8f0",
  taskShadow: "0 4px 6px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.08)",
  taskShadowHover: "0 10px 15px rgba(0,0,0,0.05), 0 4px 6px rgba(0,0,0,0.08)",
  headerBackground: "#ffffff",
  headerShadow: "0 1px 3px rgba(0,0,0,0.05)",
};

const darkThemeColors = {
  primary: "#4361ee",
  secondary: "#7b5cff",
  success: "#2ecc71",
  warning: "#f39c12",
  error: "#e74c3c",
  info: "#3498db",
  background: "#1a202c",
  card: "#2d3748",
  text: "#f7fafc",
  textSecondary: "#a0aec0",
  columnHeader: "#2d3748",
  columnBackground: "#2d3748",
  columnBackgroundHover: "#3a4556",
  taskBorder: "#4a5568",
  taskShadow: "0 4px 6px rgba(0,0,0,0.2), 0 1px 3px rgba(0,0,0,0.4)",
  taskShadowHover: "0 10px 15px rgba(0,0,0,0.2), 0 4px 6px rgba(0,0,0,0.3)",
  headerBackground: "#2d3748",
  headerShadow: "0 1px 3px rgba(0,0,0,0.3)",
};

/**
 * TaskFormModal that uses a general "allUsers" prop for assignment
 * (instead of employees). This allows assigning tasks to Admin or Employee.
 */
const TaskFormModal = ({
  open,
  onClose,
  onSave,
  initialTask,
  columnId,
  allUsers = [], // RENAMED: previously "employees"
  isDarkMode,
  isProjectSpecific = false,
  projectInfo = {},
  onAddComment,
  onEditComment,
  onDeleteComment,
  emailContext = null, // NEW: Email context for creating tasks from emails
}) => {
  const themeColors = isDarkMode ? darkThemeColors : lightThemeColors;
  const [task, setTask] = useState({
    title: "",
    creator: "",
    description: "",
    hours: 0,
    anr: "",
    files: [],
    assignedTo: null, // This will store an object { _id, displayName, userModel, ... }
    assignedDate: null,
    deadline: null,
    subTasks: [],
    comments: [],
    startTime: null,
    endTime: null,
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [selfAssign, setSelfAssign] = useState(false);
  const [newSubtask, setNewSubtask] = useState("");
  const [newComment, setNewComment] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState("");

  const fileInputRef = useRef(null);
  const creatorRef = useRef(null);
  const subtaskInputRef = useRef(null);

  const token = getToken();
  const userState = useSelector((state) => state.user);

  // If you want the ANR field to be auto-filled when in project mode
  const initialAnrRef = useRef(isProjectSpecific ? projectInfo?.anr || "" : "");

  useEffect(() => {
    if (initialTask) {
      setTask(initialTask);
      // Check if the task is self-assigned
      setSelfAssign(
        initialTask.assignedTo &&
          initialTask.assignedTo._id === userState?.user?._id
      );
    } else {
      // fresh creation
      let defaultTitle = "";
      let defaultDescription = "";

      // If creating from email, pre-fill with email data
      if (emailContext) {
        defaultTitle = `Email: ${emailContext.subject || "Kein Betreff"}`;
        defaultDescription = `Von: ${emailContext.from || "Unbekannt"}\n\n${emailContext.body || ""}`;
      }

      setTask({
        title: defaultTitle,
        creator: "",
        description: defaultDescription,
        hours: 0,
        anr: initialAnrRef.current,
        files: [],
        assignedTo: null,
        assignedDate: new Date(),
        deadline: null,
        subTasks: [],
        startTime: null,
        endTime: null,
        // Store email metadata for backend
        emailId: emailContext?.emailId || null,
        messageId: emailContext?.messageId || null,
        emailAttachments: emailContext?.emailAttachments || [],
      });
      setSelfAssign(false);
    }

    setSelectedFiles([]);
    setUploadProgress(0);
    setIsUploading(false);
    setNewSubtask("");
  }, [initialTask, open, userState, isProjectSpecific, emailContext]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTask((prev) => ({
      ...prev,
      [name]: name === "hours" ? (value === "" ? 0 : parseFloat(value)) : value,
    }));
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setTask((prev) => ({
      ...prev,
      [name]: value ? new Date(value) : null,
    }));
  };

  /***************************************************
   * 1) Handling assignment
   ***************************************************/
  const handleUserChange = (e) => {
    // If self-assign is selected, ignore dropdown changes
    if (selfAssign) return;

    const pickedId = e.target.value;
    const foundUser = pickedId
      ? allUsers.find((u) => u._id === pickedId)
      : null;

    setTask((prev) => ({
      ...prev,
      assignedTo: foundUser || null,
      assignedDate:
        prev.assignedTo === null && foundUser !== null
          ? new Date()
          : prev.assignedDate,
    }));
  };

  const handleSelfAssignToggle = (e) => {
    const isSelf = e.target.checked;
    setSelfAssign(isSelf);

    if (isSelf) {
      // when turning on self-assign => set the logged-in user
      setTask((prev) => ({
        ...prev,
        assignedTo: {
          _id: userState?.user?._id,
          displayName: `${userState?.user?.firstName} ${userState?.user?.lastName}`,
        },
        assignedDate: new Date(),
      }));
    } else {
      // turning off => clear assignment
      setTask((prev) => ({
        ...prev,
        assignedTo: null,
      }));
    }
  };

  /***************************************************
   * 2) Subtasks
   ***************************************************/
  const handleAddSubtask = () => {
    if (!newSubtask.trim()) return;

    const newSubtaskItem = {
      id: uuidv4(),
      task: newSubtask,
      status: "pending",
    };

    setTask((prev) => ({
      ...prev,
      subTasks: [...(prev.subTasks || []), newSubtaskItem],
    }));
    setNewSubtask("");

    // If we're editing an existing task, optionally do an API call
    if (initialTask && initialTask.id) {
      addSubtaskToServer(initialTask.id, newSubtask);
    }

    if (subtaskInputRef.current) {
      subtaskInputRef.current.focus();
    }
  };

  const addSubtaskToServer = async (aufgabeId, subtaskText) => {
    try {
      await axios.post(
        `${base_url}/v1/createSubTasks`,
        { aufgabeId, task: subtaskText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error("Error adding subtask:", error);
    }
  };

  const updateSubtask = (subtaskId) => {
    setTask((prev) => ({
      ...prev,
      subTasks: prev.subTasks.map((st) =>
        st._id === subtaskId
          ? {
              ...st,
              status: st.status === "completed" ? "pending" : "completed",
            }
          : st
      ),
    }));
  };

  const handleToggleSubtask = async (subtaskId) => {
    // If editing an existing task => optional API call
    if (initialTask && initialTask.id) {
      try {
        const foundSubtask = task.subTasks.find((st) => st._id === subtaskId);
        const newStatus =
          foundSubtask?.status === "completed" ? "pending" : "completed";

        // First update UI immediately for better user experience
        updateSubtask(subtaskId);

        // Then update server (with retry logic)
        const updateServer = async (retries = 3) => {
          try {
            await axios.put(
              `${base_url}/v1/updateSubTask`,
              {
                aufgabeId: initialTask.id,
                subTaskId: subtaskId,
                status: newStatus,
              },
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            console.log("Subtask status updated successfully in server");
          } catch (error) {
            console.error("Error updating subtask status:", error);
            if (retries > 0) {
              console.log(`Retrying... (${retries} attempts left)`);
              setTimeout(() => updateServer(retries - 1), 1000);
            }
          }
        };

        updateServer();
      } catch (error) {
        console.error("Error updating subtask status:", error);
      }
    } else {
      updateSubtask(subtaskId);
    }
  };

  const deleteSubtaskFromServer = async (aufgabeId, subTaskId) => {
    try {
      await axios.delete(
        `${base_url}/v1/deleteSubtask/${aufgabeId}/${subTaskId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch (error) {
      console.error("Error deleting subtask from sever:", error);
    }
  };

  const handleRemoveSubtask = async (subtaskId) => {
    try {
      // If editing an existing task => optional API call
      if (initialTask && initialTask.id)
        await deleteSubtaskFromServer(initialTask.id, subtaskId);
      setTask((prev) => ({
        ...prev,
        subTasks: prev.subTasks.filter((st) => st._id !== subtaskId),
      }));
    } catch (error) {
      console.error("Error deleting subtask:", error);
    }
  };

  /***************************************************
   * 3) File Handling
   ***************************************************/
  const handleFileSelect = (e) => {
    if (e.target.files) {
      const filesArr = Array.from(e.target.files);
      setSelectedFiles((prev) => [...prev, ...filesArr]);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // If you actually want to upload files prior to saving, you'd do that here
  const uploadFiles = async () => {
    if (selectedFiles.length === 0) return [];

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append("title", task.title);
      formData.append("description", task.description);
      if (task.assignedTo) {
        formData.append("user", task.assignedTo._id);
      }
      if (isProjectSpecific && projectInfo.id) {
        formData.append("projectId", projectInfo.id);
      }
      if (task.deadline) {
        formData.append("deadline", task.deadline.toISOString());
      }
      if (task.assignedDate) {
        formData.append("assignedDate", task.assignedDate.toISOString());
      }
      if (initialTask) {
        formData.append("status", columnId);
      }

      // Add the selected files
      selectedFiles.forEach((file) => {
        formData.append("documents", file);
      });

      const endpoint = initialTask
        ? `${base_url}/v1/updateAufgabe/${initialTask.id}`
        : `${base_url}/v1/createAufgabe`;

      const response = await axios.request({
        method: initialTask ? "put" : "post",
        url: endpoint,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(progress);
        },
      });

      setIsUploading(false);

      if (response.data && response.data.documents) {
        return response.data.documents;
      }
      return [];
    } catch (error) {
      console.error("Error uploading files:", error);
      setIsUploading(false);
      return [];
    }
  };

  /***************************************************
   * 4) Form Submission
   ***************************************************/
  const handleSubmit = async (e) => {
    e.preventDefault();

    // If you do server-side upload
    let documentIds = [];
    if (selectedFiles.length > 0) {
      documentIds = await uploadFiles();
      if (documentIds.length === 0 && selectedFiles.length > 0) {
        // means error
        return;
      }
    }

    // Combine existing + new docs
    const existingDocs = task.documents || [];
    const creatorValue = creatorRef.current ? creatorRef.current.value : "";

    // If self-assign is toggled on, ensure assignedTo is the logged-in user
    let finalTask = { ...task };
    if (selfAssign && (!finalTask.assignedTo || !finalTask.assignedTo._id)) {
      finalTask.assignedTo = {
        _id: userState?.user?._id,
        displayName: `${userState?.user?.firstName} ${userState?.user?.lastName}`,
      };
      finalTask.assignedDate = new Date();
    }

    const newTask = {
      ...finalTask,
      id: initialTask?.id || uuidv4(),
      column: columnId,
      documents: [...existingDocs, ...documentIds],
      creator: creatorValue,
    };

    // Pass back to parent
    onSave(newTask, !!initialTask);
    onClose();
  };

  /***************************************************
   * 5) Comments Handling
   ***************************************************/
  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      // If editing an existing task, make API call
      if (initialTask && initialTask.id && onAddComment) {
        const result = await onAddComment(initialTask.id, newComment.trim());
        if (result) {
          setNewComment("");
        }
      } else {
        // For a new task (not yet saved), add to local state
        setTask((prevTask) => ({
          ...prevTask,
          comments: [
            ...(prevTask.comments || []),
            {
              _id: `temp-${Date.now()}`, // Temporary ID until saved
              text: newComment.trim(),
              author: userState.user._id,
              authorModel: userState.user.userType || "Admin",
              authorName: `${userState.user.firstName} ${userState.user.lastName}`,
              createdAt: new Date(),
            },
          ],
        }));
        setNewComment("");
      }
    } catch (error) {
      console.error("Error adding comment:", error);
    }
  };

  const handleEditComment = async () => {
    if (!editCommentText.trim() || !editingCommentId) return;

    try {
      // If editing an existing task, make API call
      if (initialTask && initialTask.id && onEditComment) {
        const success = await onEditComment(
          initialTask.id,
          editingCommentId,
          editCommentText.trim()
        );
        if (success) {
          setEditingCommentId(null);
          setEditCommentText("");
        }
      } else {
        // For a new task, update local state
        setTask((prevTask) => ({
          ...prevTask,
          comments: prevTask.comments.map((comment) =>
            comment._id === editingCommentId
              ? { ...comment, text: editCommentText.trim() }
              : comment
          ),
        }));
        setEditingCommentId(null);
        setEditCommentText("");
      }
    } catch (error) {
      console.error("Error editing comment:", error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!commentId) return;

    try {
      // If editing an existing task, make API call
      if (initialTask && initialTask.id && onDeleteComment) {
        const success = await onDeleteComment(initialTask.id, commentId);
        if (!success) return;
      }

      // Update local state (regardless of API call result)
      setTask((prevTask) => ({
        ...prevTask,
        comments: prevTask.comments.filter(
          (comment) => comment._id !== commentId
        ),
      }));

      // If we were editing this comment, reset editing state
      if (editingCommentId === commentId) {
        setEditingCommentId(null);
        setEditCommentText("");
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  const calculateTimeDisplay = (milliseconds) => {
    // Convert to hours, minutes, seconds
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);

    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  /***************************************************
   * 6) Subtask Completion Calculation
   ***************************************************/
  const completedSubtasks = task.subTasks
    ? task.subTasks.filter((st) => st.status === "completed").length
    : 0;

  const subtaskCompletionPercentage =
    task.subTasks && task.subTasks.length > 0
      ? Math.round((completedSubtasks / task.subTasks.length) * 100)
      : 0;

  /***************************************************
   * RENDER
   ***************************************************/
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="task-form-modal-title"
    >
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: "650px",
          borderRadius: 4,
          p: 0,
          outline: "none",
          maxHeight: "90vh",
          overflowY: "auto",
          bgcolor: themeColors.card,
          color: themeColors.text,
          border: isDarkMode ? `1px solid ${themeColors.taskBorder}` : "none",
        }}
        elevation={isDarkMode ? 0 : 5}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2.5,
            bgcolor: themeColors.primary,
            color: "white",
            borderTopLeftRadius: "16px",
            borderTopRightRadius: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          <Typography variant="h6" id="task-form-modal-title" fontWeight={600}>
            {initialTask ? "Aufgabe bearbeiten" : "Neue Aufgabe erstellen"}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body Form */}
        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
          {/* Email Indicator - shown when creating from email */}
          {emailContext && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                bgcolor: "info.light",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <AttachFileIcon sx={{ color: "info.main" }} />
              <Box>
                <Typography variant="body2" fontWeight={600} color="info.dark">
                  Aufgabe aus E-Mail
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Von: {emailContext.from} |{" "}
                  {new Date(emailContext.date).toLocaleString("de-DE")}
                </Typography>
                {emailContext.emailAttachments?.length > 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    {emailContext.emailAttachments.length} Anhang/Anhänge
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          <Grid container spacing={2}>
            {/* Title */}
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Titel"
                name="title"
                value={task.title}
                onChange={handleChange}
                variant="outlined"
                InputProps={{ sx: { borderRadius: 2 } }}
                sx={{ mb: 2 }}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Beschreibung"
                name="description"
                value={task.description}
                onChange={handleChange}
                variant="outlined"
                multiline
                rows={4}
                InputProps={{ sx: { borderRadius: 2 } }}
                sx={{ mb: 2 }}
              />
            </Grid>

            {/* Creator (logged-in user) */}
            <Grid item xs={12} md={6}>
              <TextField
                disabled
                required
                fullWidth
                label="Erstellt von"
                name="Creator"
                inputRef={creatorRef}
                value={
                  userState?.user?.firstName + " " + userState?.user?.lastName
                }
                onChange={handleChange}
                variant="outlined"
                InputProps={{ sx: { borderRadius: 2 } }}
                sx={{ mb: 2 }}
              />
            </Grid>

            {/* Hours */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Zeitaufwand (Stunden)"
                name="hours"
                type="number"
                value={task.hours}
                onChange={handleChange}
                variant="outlined"
                InputProps={{
                  inputProps: { min: 0, step: 0.5 },
                  sx: { borderRadius: 2 },
                  endAdornment: (
                    <Box sx={{ ml: 1, color: themeColors.textSecondary }}>
                      h
                    </Box>
                  ),
                }}
                sx={{ mb: 2 }}
              />
            </Grid>

            {/* ANR (if not project-specific) */}
            {!isProjectSpecific && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="ANR"
                  name="anr"
                  value={task.anr}
                  onChange={handleChange}
                  variant="outlined"
                  InputProps={{
                    sx: { borderRadius: 2 },
                    startAdornment: (
                      <BusinessIcon
                        sx={{ mr: 1, color: themeColors.textSecondary }}
                      />
                    ),
                  }}
                  sx={{ mb: 2 }}
                />
              </Grid>
            )}

            {/* Self-assignment Toggle */}
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  mb: 3,
                  backgroundColor: isDarkMode
                    ? "rgba(67, 97, 238, 0.05)"
                    : "#f5f9ff",
                  borderColor: selfAssign
                    ? themeColors.primary
                    : isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.08)",
                  borderWidth: selfAssign ? 2 : 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: selfAssign
                          ? themeColors.primary
                          : isDarkMode
                            ? "rgba(255,255,255,0.1)"
                            : "rgba(0,0,0,0.1)",
                        color: selfAssign ? "white" : themeColors.textSecondary,
                        mr: 2,
                      }}
                    >
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Aufgabe selbst zuweisen
                      </Typography>
                      <Typography
                        variant="body2"
                        color={themeColors.textSecondary}
                      >
                        Diese Aufgabe wird Ihnen direkt zugewiesen
                      </Typography>
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selfAssign}
                        onChange={handleSelfAssignToggle}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Assignee dropdown - disabled when self-assign */}
            <Grid item xs={12}>
              <FormControl fullWidth disabled={selfAssign} sx={{ mb: 2 }}>
                <InputLabel id="user-select-label">
                  Zugewiesener Nutzer
                </InputLabel>
                <Select
                  labelId="user-select-label"
                  id="user-select"
                  value={task.assignedTo ? task.assignedTo._id : ""}
                  onChange={handleUserChange}
                  label="Zugewiesener Nutzer"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">
                    <em>Niemand</em>
                  </MenuItem>
                  {allUsers.map((u) => (
                    <MenuItem key={u._id} value={u._id}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            mr: 1,
                            fontSize: "0.75rem",
                            bgcolor: themeColors.primary,
                          }}
                        >
                          {u.displayName?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>{u.displayName}</Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {selfAssign && (
                  <FormHelperText sx={{ color: themeColors.primary }}>
                    Deaktiviert im Selbstzuweisungsmodus
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Assigned / Deadline Dates */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Zuweisungsdatum"
                type="date"
                name="assignedDate"
                value={
                  task.assignedDate
                    ? new Date(task.assignedDate).toISOString().split("T")[0]
                    : ""
                }
                onChange={handleDateChange}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  sx: { borderRadius: 2 },
                }}
                sx={{ mb: 2 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fälligkeitsdatum"
                type="datetime-local"
                name="deadline"
                value={
                  task.deadline
                    ? new Date(task.deadline).toISOString().slice(0, 16)
                    : ""
                }
                onChange={handleDateChange}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  sx: { borderRadius: 2 },
                }}
                sx={{ mb: 2 }}
              />
            </Grid>

            {/* Timer Information (if exists) */}
            {(task.startTime || task.endTime) && (
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: isDarkMode
                      ? "rgba(243, 156, 18, 0.1)"
                      : "#fff8f0",
                    borderColor: isDarkMode
                      ? "rgba(243, 156, 18, 0.3)"
                      : "#f39c12",
                    mb: 3,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <AccessTimeIcon
                      sx={{ color: themeColors.warning, mr: 1.5 }}
                    />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Timer Informationen
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Startzeit"
                        type="datetime-local"
                        name="startTime"
                        value={
                          task.startTime
                            ? new Date(task.startTime)
                                .toISOString()
                                .slice(0, 16)
                            : ""
                        }
                        onChange={handleDateChange}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          sx: { borderRadius: 2 },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Endzeit"
                        type="datetime-local"
                        name="endTime"
                        value={
                          task.endTime
                            ? new Date(task.endTime).toISOString().slice(0, 16)
                            : ""
                        }
                        onChange={handleDateChange}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          sx: { borderRadius: 2 },
                        }}
                      />
                    </Grid>

                    {task.startTime && task.endTime && (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            p: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            bgcolor: isDarkMode
                              ? "rgba(0,0,0,0.1)"
                              : "rgba(255,255,255,0.7)",
                            borderRadius: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            color={themeColors.textSecondary}
                            mr={1}
                          >
                            Gesamtzeit:
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            fontFamily="monospace"
                          >
                            {(() => {
                              const start = new Date(task.startTime);
                              const end = new Date(task.endTime);
                              const diff = end - start;
                              return calculateTimeDisplay(diff);
                            })()}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>
            )}

            {/* Subtasks */}
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: isDarkMode
                    ? "rgba(67, 97, 238, 0.05)"
                    : "#f5f9ff",
                  borderColor: isDarkMode
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(0,0,0,0.08)",
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <ListIcon sx={{ color: themeColors.primary, mr: 1.5 }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Unteraufgaben
                  </Typography>
                  {task.subTasks && task.subTasks.length > 0 && (
                    <Chip
                      label={`${completedSubtasks}/${task.subTasks.length}`}
                      size="small"
                      sx={{
                        ml: 1.5,
                        bgcolor:
                          subtaskCompletionPercentage === 100
                            ? "rgba(46, 204, 113, 0.1)"
                            : "rgba(67, 97, 238, 0.1)",
                        color:
                          subtaskCompletionPercentage === 100
                            ? themeColors.success
                            : themeColors.primary,
                        fontWeight: "bold",
                        height: 20,
                        fontSize: "0.7rem",
                      }}
                    />
                  )}
                </Box>

                {task.subTasks && task.subTasks.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={subtaskCompletionPercentage}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        mb: 1,
                        bgcolor: isDarkMode
                          ? "rgba(255,255,255,0.1)"
                          : "rgba(0,0,0,0.05)",
                        "& .MuiLinearProgress-bar": {
                          bgcolor:
                            subtaskCompletionPercentage === 100
                              ? themeColors.success
                              : themeColors.primary,
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      color={themeColors.textSecondary}
                    >
                      {subtaskCompletionPercentage}% abgeschlossen
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder="Neue Unteraufgabe hinzufügen..."
                    value={newSubtask}
                    onChange={(e) => setNewSubtask(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddSubtask();
                      }
                    }}
                    inputRef={subtaskInputRef}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      sx: { borderRadius: 2 },
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            onClick={handleAddSubtask}
                            variant="contained"
                            size="small"
                            disabled={!newSubtask.trim()}
                            sx={{
                              borderRadius: 1.5,
                              minWidth: "auto",
                              px: 1.5,
                              py: 0.5,
                              boxShadow: "none",
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {task.subTasks && task.subTasks.length > 0 ? (
                  <List
                    dense
                    sx={{
                      bgcolor: isDarkMode
                        ? "rgba(0,0,0,0.1)"
                        : "rgba(255,255,255,0.7)",
                      borderRadius: 2,
                      maxHeight: "200px",
                      overflowY: "auto",
                    }}
                  >
                    {task.subTasks.map((subtask) => (
                      <ListItem
                        key={subtask._id}
                        sx={{
                          borderBottom: `1px solid ${
                            isDarkMode
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.05)"
                          }`,
                          "&:last-child": {
                            borderBottom: "none",
                          },
                          py: 0.5,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            edge="start"
                            checked={subtask.status === "completed"}
                            onChange={() => handleToggleSubtask(subtask._id)}
                            icon={<UncheckedIcon fontSize="small" />}
                            checkedIcon={
                              <CheckCircleIcon
                                fontSize="small"
                                color="success"
                              />
                            }
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              sx={{
                                textDecoration:
                                  subtask.status === "completed"
                                    ? "line-through"
                                    : "none",
                                color:
                                  subtask.status === "completed"
                                    ? isDarkMode
                                      ? "rgba(255,255,255,0.6)"
                                      : "rgba(0,0,0,0.5)"
                                    : themeColors.text,
                              }}
                            >
                              {subtask.task}
                            </Typography>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleRemoveSubtask(subtask._id)}
                            sx={{
                              color: themeColors.error,
                              opacity: 0.7,
                              "&:hover": {
                                opacity: 1,
                              },
                              p: 0.5,
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 2,
                      color: themeColors.textSecondary,
                      bgcolor: isDarkMode
                        ? "rgba(0,0,0,0.1)"
                        : "rgba(255,255,255,0.7)",
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Noch keine Unteraufgaben vorhanden
                    </Typography>
                    <Typography variant="caption">
                      Fügen Sie Unteraufgaben hinzu, um den Fortschritt zu
                      verfolgen
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Comments Section */}
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: isDarkMode
                    ? "rgba(52, 152, 219, 0.1)"
                    : "#f0f8ff",
                  borderColor: isDarkMode
                    ? "rgba(52, 152, 219, 0.3)"
                    : "#3498db",
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <ChatBubbleOutlineIcon
                    sx={{ color: themeColors.info, mr: 1.5 }}
                  />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Kommentare
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Chip
                    label={`${task.comments?.length || 0} Kommentar(e)`}
                    size="small"
                    variant="outlined"
                    color="info"
                  />
                </Box>

                {/* Comments List */}
                <List disablePadding>
                  {task.comments && task.comments.length > 0 ? (
                    task.comments.map((comment) => (
                      <ListItem
                        key={comment._id}
                        alignItems="flex-start"
                        disablePadding
                        sx={{
                          mb: 1,
                          pb: 1,
                          borderBottom: `1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor:
                                comment.authorModel === "Admin"
                                  ? themeColors.primary
                                  : themeColors.secondary,
                              width: 36,
                              height: 36,
                            }}
                          >
                            {comment.authorName?.charAt(0).toUpperCase() || "U"}
                          </Avatar>
                        </ListItemAvatar>

                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color={themeColors.text}
                              >
                                {comment.authorName || "Unbekannt"}
                              </Typography>
                              <Typography
                                variant="caption"
                                color={themeColors.textSecondary}
                              >
                                {new Date(comment.createdAt).toLocaleString(
                                  "de-DE",
                                  {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  }
                                )}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            editingCommentId === comment._id ? (
                              <Box mt={1}>
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={2}
                                  variant="outlined"
                                  value={editCommentText}
                                  onChange={(e) =>
                                    setEditCommentText(e.target.value)
                                  }
                                  InputProps={{
                                    sx: { borderRadius: 2 },
                                  }}
                                  size="small"
                                />
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    mt: 1,
                                  }}
                                >
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      setEditingCommentId(null);
                                      setEditCommentText("");
                                    }}
                                    sx={{ mr: 1 }}
                                  >
                                    Abbrechen
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    onClick={handleEditComment}
                                  >
                                    Speichern
                                  </Button>
                                </Box>
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                component="div"
                                color={themeColors.text}
                                sx={{
                                  mt: 1,
                                  whiteSpace: "pre-wrap",
                                  backgroundColor: isDarkMode
                                    ? "rgba(255,255,255,0.05)"
                                    : "rgba(0,0,0,0.02)",
                                  p: 1.5,
                                  borderRadius: 2,
                                }}
                              >
                                {comment.text}
                              </Typography>
                            )
                          }
                        />

                        {/* Comment Actions */}
                        {editingCommentId !== comment._id && (
                          <Box
                            sx={{
                              display: "flex",
                              ml: 2,
                              alignItems: "center",
                            }}
                          >
                            <Tooltip title="Bearbeiten">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingCommentId(comment._id);
                                  setEditCommentText(comment.text);
                                }}
                                sx={{
                                  color: themeColors.primary,
                                  opacity: 0.7,
                                  "&:hover": { opacity: 1 },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Löschen">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      "Möchten Sie diesen Kommentar wirklich löschen?"
                                    )
                                  ) {
                                    handleDeleteComment(comment._id);
                                  }
                                }}
                                sx={{
                                  color: themeColors.error,
                                  opacity: 0.7,
                                  "&:hover": { opacity: 1 },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </ListItem>
                    ))
                  ) : (
                    <Box
                      sx={{
                        py: 3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        color: themeColors.textSecondary,
                        backgroundColor: isDarkMode
                          ? "rgba(255,255,255,0.02)"
                          : "rgba(0,0,0,0.02)",
                        borderRadius: 2,
                      }}
                    >
                      <ChatBubbleOutlineIcon
                        sx={{ fontSize: "2rem", mb: 1, opacity: 0.6 }}
                      />
                      <Typography variant="body2">
                        Keine Kommentare vorhanden
                      </Typography>
                    </Box>
                  )}
                </List>

                {/* Add New Comment */}
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    label="Neuen Kommentar hinzufügen"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Geben Sie Ihren Kommentar ein..."
                    InputProps={{
                      sx: { borderRadius: 2 },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            color="primary"
                            onClick={handleAddComment}
                            disabled={!newComment.trim()}
                            sx={{ mr: -1 }}
                          >
                            <SendIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Paper>
            </Grid>

            {/* File Upload Section (optional) */}
            {/* If you want to keep the logic for uploading, keep this block */}
            {/* If you skip server side uploads, you can remove it */}
            {/* ... same as your code ... */}
          </Grid>

          <Divider
            sx={{
              my: 3,
              borderColor: isDarkMode
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.08)",
            }}
          />

          {/* Footer Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Button
              variant="outlined"
              color="inherit"
              onClick={onClose}
              sx={{
                borderRadius: 2,
                borderColor: isDarkMode
                  ? "rgba(255,255,255,0.2)"
                  : "rgba(0,0,0,0.2)",
                color: themeColors.textSecondary,
                px: 3,
                "&:hover": {
                  backgroundColor: isDarkMode
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.05)",
                  borderColor: isDarkMode
                    ? "rgba(255,255,255,0.3)"
                    : "rgba(0,0,0,0.3)",
                },
              }}
              startIcon={<CloseIcon />}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                borderRadius: 2,
                boxShadow: "none",
                px: 4,
                py: 1.25,
                fontWeight: 600,
                backgroundColor: themeColors.primary,
                "&:hover": {
                  backgroundColor: "#3651d1",
                  boxShadow: "none",
                },
              }}
              disabled={isUploading}
              startIcon={
                isUploading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
            >
              {isUploading
                ? "Wird hochgeladen..."
                : initialTask
                  ? "Speichern"
                  : "Erstellen"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default TaskFormModal;
